import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HeartHandshake, Loader2, Download, ArrowUpDown } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";

interface Row {
  id: string; name: string; devices: number; active: number; ai: number; notif: number;
  users: number; activeUsers: number; revenue: number; arpd: number;
}
type Key = keyof Row;

const fmtGBP = (n: number) => "£" + n.toLocaleString("en-GB", { maximumFractionDigits: 0 });
const WEEK = 7 * 24 * 3600 * 1000;
// Devices held by the internal Production organisation (see src/pages/Production.tsx) are ours, not customers'.
const PRODUCTION_ORG_ID = "7a4a3ed7-f614-4e4a-9eeb-dba745740c35";

async function fetchAll<T>(table: string, cols: string): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await (supabase as any).from(table).select(cols).range(from, from + 999);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < 1000) return out;
  }
}

export default function CustomerSuccess() {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ k: Key; asc: boolean }>({ k: "devices", asc: false });

  const { data, isLoading, error } = useQuery({
    queryKey: ["customer-success"],
    queryFn: async () => {
      const [orgs, devices, profiles, deals] = await Promise.all([
        fetchAll<any>("organizations", "id,name"),
        fetchAll<any>("device_details", "organization_id,device_status,device_last_updated,notification_ai_model,enable_notifications,notification_email"),
        fetchAll<any>("profiles", "organization_id,last_seen_at,status"),
        fetchAll<any>("crm_deals", "organization_id,status,value,archived,deal_data"),
      ]);
      const cutoff = Date.now() - WEEK;
      const rows = new Map<string, Row>();
      for (const o of orgs) rows.set(o.id, { id: o.id, name: o.name || "—", devices: 0, active: 0, ai: 0, notif: 0, users: 0, activeUsers: 0, revenue: 0, arpd: 0 });
      for (const d of devices) {
        const r = d.organization_id && rows.get(d.organization_id); if (!r) continue;
        r.devices++;
        // Active = camera is talking: checked in within the last 48 hours, whatever its mode (Record, Retry, On Demand…), excluding demo units.
        const seen = d.device_last_updated ? new Date(d.device_last_updated).getTime() : NaN;
        const isDemo = (d.device_status || "").toLowerCase() === "demo";
        if (!isDemo && !isNaN(seen) && Date.now() - seen < 2 * 24 * 3600 * 1000) r.active++;
        // "Enable AI" in camera settings is stored as enable_notifications (model is cleared when off).
        if (d.enable_notifications && d.notification_ai_model) r.ai++;
        // Notifications = AI on and at least one recipient email set.
        if (d.enable_notifications && (d.notification_email || "").trim()) r.notif++;
      }
      for (const p of profiles) {
        const r = p.organization_id && rows.get(p.organization_id); if (!r) continue;
        if (p.status === "disabled") continue;
        r.users++;
        if (p.last_seen_at && new Date(p.last_seen_at).getTime() >= cutoff) r.activeUsers++;
      }
      for (const d of deals) {
        const r = d.organization_id && rows.get(d.organization_id); if (!r) continue;
        if (d.status !== "won" || d.archived) continue;
        // Yearly revenue: spread multi-year contracts over their commitment years; short-term (<1 year) deals count in full.
        const years = d.deal_data?.term_type === "short" ? 1 : Math.max(1, Number(d.deal_data?.commitment_years) || 1);
        r.revenue += (Number(d.value) || 0) / years;
      }
      return [...rows.values()]
        .filter(r => r.id !== PRODUCTION_ORG_ID)
        .map(r => ({ ...r, arpd: r.revenue / (r.devices || 1) }))
        .filter(r => r.devices > 0);
    },
  });

  const rows = useMemo(() => {
    const list = (data || []).filter(r => r.name.toLowerCase().includes(q.toLowerCase()));
    return [...list].sort((a, b) => {
      const va = a[sort.k], vb = b[sort.k];
      const c = typeof va === "string" ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sort.asc ? c : -c;
    });
  }, [data, q, sort]);

  const t = useMemo(() => rows.reduce((s, r) => ({
    customers: s.customers + 1, devices: s.devices + r.devices, active: s.active + r.active, ai: s.ai + r.ai,
    notif: s.notif + r.notif, users: s.users + r.users, activeUsers: s.activeUsers + r.activeUsers, revenue: s.revenue + r.revenue,
  }), { customers: 0, devices: 0, active: 0, ai: 0, notif: 0, users: 0, activeUsers: 0, revenue: 0 }), [rows]);

  const cols: [Key, string, (r: Row) => string][] = [
    ["name", "Customer", r => r.name],
    ["devices", "Devices", r => String(r.devices)],
    ["active", "Active", r => String(r.active)],
    ["ai", "AI enabled", r => String(r.ai)],
    ["notif", "Notifications", r => String(r.notif)],
    ["users", "Users", r => String(r.users)],
    ["activeUsers", "Active users (7d)", r => `${r.activeUsers} / ${r.users}`],
  ];

  const exportCsv = () => {
    const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const csv = [cols.map(c => esc(c[1])).join(","), ...rows.map(r => cols.map(c => esc(c[2](r))).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `customer-success-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const pct = (n: number) => t.devices ? `${Math.round((n / t.devices) * 100)}%` : "—";
  const kpis: [string, string][] = [
    ["Customers", String(t.customers)],
    ["Total devices", String(t.devices)],
    ["Active devices", String(t.active)],
    ["AI enabled", pct(t.ai)],
    ["Notifications on", pct(t.notif)],
    ["Active users (7d)", `${t.activeUsers} / ${t.users}`],
  ];

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <FixedPageHeader title="Customer Success" description="Devices, usage and engagement per customer" icon={HeartHandshake} />
          <div className="p-6 space-y-6" style={{ paddingTop: "8.5rem" }}>
            {isLoading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin" /></div>
              : error ? <p className="text-destructive">Could not load data: {(error as Error).message}</p> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                  {kpis.map(([l, v], i) => (
                    <Card key={l} title={i === 3 ? `${t.ai} of ${t.devices} devices` : i === 4 ? `${t.notif} of ${t.devices} devices` : undefined}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{l}</p><p className="text-xl font-semibold mt-1">{v}</p></CardContent></Card>
                  ))}
                </div>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
                    <CardTitle>Customers</CardTitle>
                    <div className="flex gap-2">
                      <Input placeholder="Search customer…" value={q} onChange={e => setQ(e.target.value)} className="w-56" />
                      <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-2" />CSV</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader><TableRow>
                        {cols.map(([k, l]) => (
                          <TableHead key={k} className={k === "name" ? "" : "text-right"}>
                            <button className="inline-flex items-center gap-1" onClick={() => setSort(s => ({ k, asc: s.k === k ? !s.asc : k === "name" }))}>
                              {l}<ArrowUpDown className="h-3 w-3" />
                            </button>
                          </TableHead>
                        ))}
                      </TableRow></TableHeader>
                      <TableBody>
                        {rows.map(r => (
                          <TableRow key={r.id}>
                            {cols.map(([k, , f]) => <TableCell key={k} className={k === "name" ? "font-medium" : "text-right"}>{f(r)}</TableCell>)}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <p className="text-xs text-muted-foreground mt-4">Active = cameras in Record status. Active users = signed in within the last 7 days. Revenue = won CRM deal value ÷ devices.</p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
